import { raisso } from './raisso.js';
import { refresh as raiSsoRefreshEvent, login as raiSsoLoginEvent, userUpdate as raiUserUpdate, userDataUpdate as raiUserDataUpdate, event$ as _event$ } from './events.js';
import { DOMAIN_API_KEY } from './definitions.js';
import { InvalidOperationError, InvalidResponseError, NeedLoginError, NeedLogoutError, NeedRegistrationError, RaissoError, RequestNotAllowedError, errorFactory } from './responses.js';
import { checkPassword } from './password-checker.js';
import { checkEmail } from './email-checker.js';
import { get as getCookie } from 'es-cookie';
import { GigyaLoginCallback, RaiSsoProfile } from './profile.js';
import LocalStorageService from './storage-service.js';
import { DateTime } from 'luxon'
import { AppUpdateRequest, DeviceController, GoogleOauthController, LoginController, PrivacyController, RegistrationController, TokenController, UserController, ParentalControlController } from './controllers/index.js'
import { DateTimeString, DeviceRequest, DownloadResponse, GenericResponse, LoginResponseLogin, OauthCodeResponse, OauthLoginRequest, PrivacyResponse, PubblicitaProfilataResponse, RegistrationRequest, ReportsRequest, UpdateDataRequest, UpdatePrivacyDataRequest } from './controllers/service-models.js'
import { dispatchEvent, broadcast } from './utils.js';
import { Result, R } from '@mobily/ts-belt'

const localStorageService = LocalStorageService.getService();

type TUserUpdateName = 'profile_update' | 'user_data_update' | 'user_privacy_update';

export class RaiSsoProfileManager {

  get broadcastChannel() {
    return 'rai:raisso';
  }

  canRefreshUserDetails = true;

  public get events$() { return _event$.asObservable(); }

  async userInfoUpdate(detail: AppUpdateRequest, type: 'profile_update'): Promise<GenericResponse>;
  async userInfoUpdate(detail: UpdateDataRequest, type: 'user_data_update'): Promise<GenericResponse>;
  async userInfoUpdate(detail: UpdatePrivacyDataRequest, type: 'user_privacy_update'): Promise<GenericResponse>;
  async userInfoUpdate(detail: AppUpdateRequest | UpdateDataRequest| UpdatePrivacyDataRequest, type: TUserUpdateName) {
    let response: Result<GenericResponse, RaissoError>;
    try {
      switch(type) {
        case 'profile_update': // user_data
          response = await UserController.instance.appUpdate(detail as AppUpdateRequest)
          break;
        case 'user_data_update':
          response = await UserController.instance.update(detail as UpdateDataRequest)
          break;
        case 'user_privacy_update': // user_privacy
          response = await PrivacyController.instance.update(detail as UpdatePrivacyDataRequest);
          break;
        default:
          throw new TypeError('Invalid type ' + type);
      }
      const resp = R.getExn(response);
      if (type === "user_privacy_update" || type === "user_data_update") {
        const json = this.getUserProfile()?.toJSON();
        if (json) {
          json.user_update?.forEach((el, index) => {
            if (el.name === type) json.user_update?.splice(index, 1);
          });
          const p = RaiSsoProfile.fromJSON(json);
          localStorageService.setProfile(p);
        }
      }
      return resp;
    } catch(error){
      if (error instanceof RaissoError) {
        throw InvalidOperationError.fromRaisso(error);
      }
      if (error instanceof Error) throw error;
      console.log('[profile-manager] userInfoUpdate:', error);
      throw InvalidOperationError.fromRaisso(errorFactory(RaissoError, 'Opps! Qualcosa è andato storto', { cause: error, context: { data: JSON.stringify({ type, detail }) } }))
    }
  }

  getUserUpdate(name?: string) {
    const user_updates = this.getUserProfile()?.user_update;
    if (!name) {
      return user_updates
    } else {
      return user_updates?.find(u => u.name === name);
    }
  }

  getUserDataUpdate() {
    return this.getUserProfile()?.user_data_update;
  }

  async refreshUserDetails(): Promise<RaiSsoProfile> {
    if (!this.canRefreshUserDetails) {
      throw RequestNotAllowedError.fromRaisso(errorFactory(RaissoError, 'Cannot refresh user details'));
    }
    this.canRefreshUserDetails = false;
    try {
      const accessToken = localStorageService.getAccessToken()
      if (!accessToken) {
        throw NeedLoginError.fromRaisso(errorFactory(RaissoError, 'User not logged in', { context: { needLogin: true } }));
      }
      const serviceData = await UserController.instance.infox()
      const data = R.getExn(serviceData);
      if (!data){
        throw InvalidResponseError.fromRaisso(errorFactory(RaissoError, 'User details is undefined'));
      }
      const { state } = data as unknown as { state: number };
      if (state === 3) {
        throw NeedLogoutError.fromRaisso(errorFactory(RaissoError, "Requested logout and login"));
      }

      const userProfile = localStorageService.getProfile();
      data.photoURL = data.photoURL ?? userProfile?.photoURL ?? undefined;
      const profile = RaiSsoProfile.fromLogin({
        ente_accreditato: userProfile?.user_type === 'ente',
        social: userProfile?.social,
        raisso: data
      })

      localStorageService.setProfile(profile);
      if (profile.user_update) {
        _event$.next(new CustomEvent(raiUserUpdate, { detail: profile, bubbles: true, cancelable: true }));
        dispatchEvent(raiUserUpdate, profile);
      }
      if (profile.user_data_update) {
        _event$.next(new CustomEvent(raiUserDataUpdate, { detail: profile, bubbles: true, cancelable: true }));
        dispatchEvent(raiUserDataUpdate, profile);
      }
      if (userProfile) {
        dispatchEvent(raiSsoRefreshEvent, profile);
      } else {
        dispatchEvent(raiSsoLoginEvent, profile);
        broadcast(this.broadcastChannel, { action: 'login' });
      }
      this.canRefreshUserDetails = true;
      return profile;
    }
    catch (error) {
      console.error(error);
      if (error instanceof RaissoError) {
        throw InvalidOperationError.fromRaisso(error);
      }
      if (error instanceof Error) { throw error; }
      throw InvalidOperationError.fromRaisso(errorFactory(RaissoError, 'refreshUserDetails'))
    }
  }

  async confirmUserByUrl(): Promise<GenericResponse> {
    const u = new URL(window.location.href);

    try {
      const resp = await UserController.instance.activation(u.search)
      const response = R.getExn(resp);
      if (response.response === 'KO') {
        throw InvalidResponseError.fromRaisso(errorFactory(RaissoError, response, { context: { data: u.search } }))
      }
      return response
    }
    catch (e) {
      if (e instanceof RaissoError) { throw InvalidOperationError.fromRaisso(e) }
      if (e instanceof Error) { throw e; }
      console.error('[profile-manager] confirmUserByUrl:', e);
      throw InvalidOperationError.fromRaisso(errorFactory(RaissoError, 'Opps! Qualcosa è andato storto', {
        cause: e, context: { data: u.search }
      }))
    }
  }

  async associateUserByUrl(): Promise<GenericResponse> {
    const u = new URL(window.location.href);
    const t = u.searchParams.get("t") ?? '';
    try {
      const response = await UserController.instance.socialPair(t)
      const resp = R.getExn(response);
      if (resp.response === 'KO') {
        throw InvalidResponseError.fromRaisso(errorFactory(RaissoError, resp, { context: { data: t } }))
      }
      return resp;
    } catch (e) {
      if (e instanceof RaissoError) { throw InvalidOperationError.fromRaisso(e) }
      if (e instanceof Error) { throw e }
      throw InvalidOperationError.fromRaisso(errorFactory(RaissoError, 'Opps! Qualcosa è andato storto', { cause: e, context: { data: t } }))
    }
  }

  async getPrivacyPolicyInfo(): Promise<PrivacyResponse> {
    try {
      const response = await PrivacyController.instance.get()
      const data =R.getExn(response);
      const profile = localStorageService.getProfile();
      if (profile) {
        profile.setPrivacyData(data)
        localStorageService.setProfile(profile);
      }
      return data;
    }
    catch (e) {
      if (e instanceof RaissoError) { throw InvalidOperationError.fromRaisso(e) }
      if (e instanceof Error) { throw e; }
      console.error(e);
      throw InvalidOperationError.fromRaisso(errorFactory(RaissoError, 'Opps! Qualcosa è andato storto', { cause: e }))
    }
  }

  async setPrivacyPolicyInfo(data: UpdatePrivacyDataRequest) {
    try {
      const response = await PrivacyController.instance.update(data)
      const resp =R.getExn(response);
      if (resp.response === 'KO') {
        throw InvalidResponseError.fromRaisso(errorFactory(RaissoError, resp, { context: { data: JSON.stringify(data) } }))
      }
      return resp;
    }
    catch (error) {
      if (error instanceof RaissoError) { throw InvalidOperationError.fromRaisso(error) }
      if (error instanceof Error) { throw error; }
      console.error('[profile-manager] setPrivacyPolicyInfo', error);
      throw InvalidOperationError.fromRaisso(errorFactory(RaissoError, 'Opps! Qualcosa è andato storto', { cause: error, context: { data: JSON.parse(JSON.stringify(data)) } }))
    }
  }

  async setPersonalizzazione(personalization: boolean) {
    try {
      const response = await PrivacyController.instance.savePersonalizzazione({ personalizzazione: personalization })
      const resp = R.getExn(response);
      if (resp.response === 'KO') {
        throw InvalidResponseError.fromRaisso(errorFactory(RaissoError, resp, { context: { data: personalization } }))
      }
      const p = this.getUserProfile()
      if (p) {
        p.setPrivacyData({ personalizzazione: personalization })
        localStorageService.setProfile(p)
      }
      return resp
    } catch (error) {
      if (error instanceof RaissoError) { throw InvalidOperationError.fromRaisso(error) }
      if (error instanceof Error) { throw error; }
      console.error('[profile-manager] setPersonalizzazione:', error);
      throw InvalidOperationError.fromRaisso(errorFactory(RaissoError, 'Opps! qualcosa è andato storto', { cause: error, context: { data: personalization } }))
    }
  }

  async getPubblicitaProfilata(): Promise<PubblicitaProfilataResponse> {
    try {
      const response = await PrivacyController.instance.pubblicitaProfilata()
      const data = R.getExn(response);
      const profile = localStorageService.getProfile();
      if (profile) {
        const { privacy_Pubblicita_isAccepted, pubblicitaProfilata } = data
        profile.setPrivacyData({
          pubblicitaProfilata: pubblicitaProfilata,
          privacy_Pubblicita_isAccepted: privacy_Pubblicita_isAccepted,
        })
        localStorageService.setProfile(profile);
      }
      return data;
    }
    catch (e) {
      if (e instanceof RaissoError) { throw InvalidResponseError.fromRaisso(e) }
      if (e instanceof Error) { throw e; }
      console.error('[profile-manager] getPubblicitaProfilata:', e);
      throw InvalidOperationError.fromRaisso(errorFactory(RaissoError, 'Opps! Qualcosa è andato storto', { cause: e }))
    }
  }

  async setPubblicitaProfilata(checked: boolean) {
    try {
      const response = await PrivacyController.instance.savePubblicitaProfilata({ pubblicitaProfilata: checked })
      const data = R.getExn(response);
      return data
    }
    catch (error) {
      if (error instanceof RaissoError) { throw InvalidResponseError.fromRaisso(error) }
      if (error instanceof Error) { throw error; }
      console.error('[profile-manager] setPubblicitaProfilata:', error);
      throw InvalidOperationError.fromRaisso(errorFactory(RaissoError, 'Opps! Qualcosa è andato storto', { cause: error, context: { data: checked } }))
    }
  }

  hasPubblicitaProfilata() {
    if (!raisso.checkLogin()) { return false; }
    const p = this.getUserProfile();
    return p?.pubblicitaProfilata && p.pubblicitaProfilataIsAccepted;
  }

  isSocialInAppBrowser() {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const ua = navigator.userAgent || navigator.vendor || (window as any).opera;
    return (ua.indexOf("FBAN") > -1) || (ua.indexOf("FBAV") > -1) || (ua.indexOf('Instagram') > -1);
  }

  isMobile(){
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|Windows Phone/i.test(
      navigator.userAgent,
    );
  }

  getUserProfile() {
    return localStorageService.getProfile();
  }

  isEnte() {
    const profile = this.getUserProfile();
    return profile?.user_type === 'ente';
  }

  async sendReport(data: ReportsRequest) {
    try {
      const response = await UserController.instance.reports(data)
      const resp = R.getExn(response);
      return resp;
    }
    catch (e) {
      if (e instanceof RaissoError) { throw InvalidResponseError.fromRaisso(e) }
      if (e instanceof Error) { throw e; }
      console.error('[profile-manager] sendReport:', e);
      throw InvalidOperationError.fromRaisso(errorFactory(RaissoError, 'Opps! Qualcosa è andato storto', { cause: e, context: { data: JSON.stringify(data) } }))
    }
  }

  async sendRecover(email: string) {
    if (!checkEmail(email)) {
      throw RequestNotAllowedError.fromRaisso(errorFactory(RaissoError, 'Email non valida', { context: { data: email } }))
    }
    try {
      const response = await UserController.instance.credentialRecovery(email)
      const resp = R.getExn(response);
      return resp;
    }
    catch (e) {
      if (e instanceof RaissoError) { throw InvalidResponseError.fromRaisso(e) }
      if (e instanceof Error) { throw e; }
      console.error('[profile-manager] sendRecover:', e);
      throw InvalidOperationError.fromRaisso(errorFactory(RaissoError, 'Opps! Qualcosa è andato storto', { cause: e, context: { data: email } }))
    }
  }

  async changePassword(oldPassword: string | undefined, newPassword: string, token?: string) {
    if (!checkPassword(newPassword)) {
      throw RequestNotAllowedError.fromRaisso(errorFactory(RaissoError, 'Password non valida', { context: { data: JSON.stringify({ new_password: newPassword }) } }))
    }
    try {
      let response: Result<GenericResponse, RaissoError>;
      if (oldPassword) {
        const authToken = localStorageService.getAccessToken();
        if (!authToken) {
          throw NeedLoginError.fromRaisso(errorFactory(RaissoError, 'Richiesta la login', { context: { needLogin: true } }))
        }
        response = await UserController.instance.credentialTokenChange(oldPassword, newPassword)
      } else if (token) {
        response = await UserController.instance.credentialChange(newPassword, token)
      } else {
        throw RequestNotAllowedError.fromRaisso(errorFactory(RaissoError, 'Parametri non validi', { context: { data: JSON.stringify({ new_password: newPassword, old_password: oldPassword, token }) } }))
      }
      const resp = R.getExn(response);
      return resp;
    }
    catch (e) {
      if (e instanceof RaissoError) { throw InvalidResponseError.fromRaisso(e) }
      if (e instanceof Error) { throw e; }
      console.error('[profile-manager] changePassword:', e);
      throw InvalidOperationError.fromRaisso(errorFactory(RaissoError, 'Opps! Qualcosa è andato storto', { cause: e, context: { data: JSON.stringify({ new_password: newPassword, old_password: oldPassword, token})} }))
    }
  }

  async resetPassword(email?: string): Promise<GenericResponse> {
    if (email && !checkEmail(email)) {
      throw RequestNotAllowedError.fromRaisso(errorFactory(RaissoError, 'Email non valida', { context: { data: email } }))
    }
    try {
      let response: Result<GenericResponse, RaissoError>;
      if (email) {
        response = await UserController.instance.credentialRequestChange(email)
      } else {
        const token = localStorageService.getAccessToken()
        if (!token) {
          throw NeedLoginError.fromRaisso(errorFactory(RaissoError, 'Richiesta la login', { context: { needLogin: true  }}))
        }
        response = await UserController.instance.credentialRequestTokenChange()
      }
      const resp = R.getExn(response);
      return resp;
    } catch (e) {
      if (e instanceof RaissoError) { throw InvalidResponseError.fromRaisso(e) }
      if (e instanceof Error) { throw e; }
      console.error('[profile-manager] resetPassword:', e);
      throw InvalidOperationError.fromRaisso(errorFactory(RaissoError, 'Opps! Qualcosa è andato storto', { cause: e, context: { data: email } }))
    }
  }

  async userServices() {
    try {
      const response = await UserController.instance.service()
      const resp = R.getExn(response);
      return resp;
    } catch (e) {
      if (e instanceof RaissoError) { throw InvalidResponseError.fromRaisso(e) }
      if (e instanceof Error) { throw e; }
      console.error('[profile-manager] userServices:', e);
      throw InvalidOperationError.fromRaisso(errorFactory(RaissoError, 'Opps! qualcosa è andato storto', { cause: e }))
    }
  }

  async userServiceActivities(service: string) {
    try {
      const response = await UserController.instance.trackDomain(service)
      const resp = R.getExn(response);
      return resp;
    }
    catch (e) {
      if (e instanceof RaissoError) { throw InvalidResponseError.fromRaisso(e) }
      if (e instanceof Error) { throw e; }
      console.error('[profile-manager] userServicesAttivities:', e);
      throw InvalidOperationError.fromRaisso(errorFactory(RaissoError, 'Opps! Qualcosa è andato storto', { cause: e, context: { data: service }}))
    }
  }

  async exportUserInfo(method: "request" | "status"): Promise<DownloadResponse>;
  async exportUserInfo(method: "download", downloadToken: string): Promise<string>;
  async exportUserInfo(method: "request" | "status" | "download", downloadToken?: string) {
    const token = localStorageService.getAccessToken();
    const refreshToken = localStorageService.getRefreshToken();
    if (!token && !refreshToken) {
      throw NeedLoginError.fromRaisso(errorFactory(RaissoError, 'Richiesta la login', { context: { needLogin: true } }))
    }
    let response: Result<GenericResponse | DownloadResponse | string, RaissoError>;
    try {
      switch (method) {
        case 'request':
          response = await UserController.instance.trackRequest()
          break;
        case 'status':
          response = await UserController.instance.trackExport()
          break;
        case 'download':
          if (!downloadToken) {
            throw RequestNotAllowedError.fromRaisso(errorFactory(RaissoError, 'Parametro obbligatorio mancante', { context: { data: JSON.stringify({ method, downloadToken })}}))
          }
          response = await UserController.instance.trackDownload(downloadToken)
          break;
      }
      const resp = R.getExn(response);
      return resp;
    }
    catch (e) {
      if (e instanceof RaissoError) { throw InvalidResponseError.fromRaisso(e) }
      if (e instanceof Error) { throw e; }
      console.error('[profile-manager] exportUserInfo', e);
      throw InvalidOperationError.fromRaisso(errorFactory(RaissoError, 'Opps! Qualcosa è andato storto', { cause: e, context: { data: JSON.stringify({ method, downloadToken }) } }))
    }
  }

  /**
   * Nuovo metodo con x-auth-token in sostituzione di sendUpdateProfile (no GDPR compliant)
   */
  async sendProfileUpdate(data: AppUpdateRequest) {
    try {
      const response = await UserController.instance.appUpdate(data)
      const resp = R.getExn(response);
      const { firstName, lastName, gender, image, birthDate } = data
      const date = DateTime.fromFormat(birthDate, 'dd/MM/yyyy')
      const p = this.getUserProfile()
      if (p && date.isValid) {
        const json = p.toJSON()
        const profile = RaiSsoProfile.fromJSON(
          Object.assign(json, {
            firstName,
            lastName,
            gender,
            birthDate: date.toFormat('dd-MM-yyyy')
          } as Pick<typeof json, 'firstName' | 'lastName' | 'gender' | 'birthDate'>,
          image ? { photoURL: image } : {})
        )
        localStorageService.setProfile(profile);
      }
      return resp;
    }
    catch (e) {
      if (e instanceof RaissoError) { throw InvalidResponseError.fromRaisso(e) }
      if (e instanceof Error) { throw e; }
      console.error('[profile-manager] sendProfileUpdate:', e);
      throw InvalidOperationError.fromRaisso(errorFactory(RaissoError, 'Opps! Qualcosa è andato storto', { cause: e, context: { data }}))
    }
  }

  genericLogin(mail: string, password: string, isEnte = false) {
    return this.sendLogin(mail, password, DOMAIN_API_KEY, false, isEnte);
  }

  async sendLogin(email: string, password: string, domainApiKey: string, onlyCkeck: true, isEnte?: boolean): Promise<LoginResponseLogin>;
  async sendLogin(email: string, password: string, domainApiKey: string, onlyCkeck?: false, isEnte?: boolean): Promise<RaiSsoProfile>;
  async sendLogin(email: string, password: string, domainApiKey: string, onlyCheck = false, isEnte = false) {
    try {
      /* if (!checkEmail(email)) {
        console.error('[profile-manager] sendLogin: Mail non valida');
        throw RequestNotAllowedError.fromRaisso(errorFactory(RaissoError, 'Mail non valida', { context: { data: { email, password, domainApiKey, isEnte } } }))
      }
      if (!checkPassword(password)) {
        console.error('[profile-manager] sendLogin: Password non valida');
        throw RequestNotAllowedError.fromRaisso(errorFactory(RaissoError, 'Password non valida', { context: { data: { email, password, domainApiKey, isEnte } } }))
      } */
      const resp = await LoginController.instance.site(domainApiKey, email, password, undefined, isEnte)
      const data = R.getExn(resp);

      const { response, detail, message, title } = data;
      if (response === 'KO') {
        const r: GenericResponse = {
          detail,
          message: message ?? 'Opps! Qualcosa è andato storto',
          response,
          title
        }
        throw InvalidResponseError.fromRaisso(errorFactory(RaissoError, r, { context: { data: { email, password, domainApiKey, isEnte } }}))
      }
      if (onlyCheck) { return data; }
      return this.logUser(data);
    } catch (e) {
      if (e instanceof RaissoError) { throw InvalidResponseError.fromRaisso(e) }
      if (e instanceof Error) { throw e; }
      console.error('[profile-manager] sendLogin:', e);
      throw InvalidOperationError.fromRaisso(errorFactory(RaissoError, 'Opps! Qualcosa è andato storto', { cause: e,  context: { data: { email, password, domainApiKey, isEnte } } }))
    }
  }

  socialLogin(mode: string) {
    return new Promise((resolve, reject) => {
      const authFlow = 'redirect'; /*(this.isSocialInAppBrowser() || this.isMobile()) ? 'redirect' : 'popup';*/
      const params = {
        provider: mode,
        authFlow:authFlow,
        callback: (resp: GigyaLoginCallback) => {
          if (resp.errorCode !== 0) {
            reject(InvalidResponseError.fromRaisso(errorFactory(RaissoError, resp.errorMessage, { context: { data: mode } })))
          }
          else {
            resolve(this.sendSocialLogin(resp));
          }
        }
      };
      window.gigya.socialize.login(params);
    });
  }

  checkGigyaLogin(){
    const params = new URLSearchParams(document.location.search);

    if(params.get("gig_events") === 'socialize.login' &&
      params.get("errorCode") === '0' && !raisso.checkLogin()){
      window.gigya.socialize.getUserInfo({
        callback: this.onGigyaLogin.bind(this)
      })
    }
  }

  onGigyaLogin(resp: GigyaLoginCallback){
    if (resp.errorCode !== 0) {
      throw InvalidResponseError.fromRaisso(errorFactory(RaissoError, resp.errorMessage))
    } else {
      try {
        const url = document.location.protocol + '//' + document.location.host + document.location.pathname;
        window.history.replaceState(null, document.title, url);
      } catch (e) {
        //pass
      }
      this.sendSocialLogin(resp);
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  async sendSocialLogin(data: GigyaLoginCallback) {
    if (!data) {
      console.error('[profile-manager] sendSocialLogin: Parametri obbligatori mancanti (data)')
      throw RequestNotAllowedError.fromRaisso(errorFactory(RaissoError, 'Parametri obbligatori mancanti', { context: { data: JSON.stringify(data) } }))
    }
    const _data: GigyaLoginCallback & { domainApiKey: string } = {...data, domainApiKey: DOMAIN_API_KEY };
    try {
      const response = await LoginController.instance.social(JSON.stringify(_data))
      const respData = R.getExn(response);
      const { detail } = respData;
      switch (detail) {
        case "NOUSER": {
          const { firstName, lastName, email, UID, loginProvider } = data.user;
          dispatchEvent("rai:ssoWidget-openRequest", {
            mode: 'registrationSocial',
            data: {
              firstName,
              lastName,
              email,
              socialUID: UID,
              socialProvider: loginProvider
            }
          });
          return respData;
        }
        case "DELETEREQUIRED":
          dispatchEvent("rai:ssoWidget-openRequest", {
            mode: 'registration'
          });
          throw NeedRegistrationError.fromRaisso(errorFactory(RaissoError, 'necessaria la regisrazione', { context: { needRegistration: true }}))
        case "ACTIVATIONREQUIRED":
          return respData;
        case "SOCIAL_OVER_NOUSER_NO_ACTIVATION_MAIL":
        default:
          return this.logUser(respData);
      }
    } catch (e) {
      if (e instanceof RaissoError) { throw InvalidResponseError.fromRaisso(e) }
      if (e instanceof Error) { throw e; }
      console.error('[profile-manager] sendSocialLogin:', e);
      throw InvalidOperationError.fromRaisso(errorFactory(RaissoError, 'Opps! Qualcosa è andato storto', { cause: e, context: { data: JSON.stringify(data) } }))
    }
  }

  logUser(data: LoginResponseLogin) {
    return this._setUserObject(data);
  }

  private async _setUserObject(data: LoginResponseLogin) {
    const { raisso, authorization, refreshToken, social, ente_accreditato, user_data_update, user_update } = data;
    if (!raisso || !authorization || !refreshToken) {
      const e = errorFactory(RaissoError, "Login non valida", { context: { data: JSON.stringify(data) }})
      throw NeedLoginError.fromRaisso(e);
    }
    if (!raisso.photoURL) {
      raisso.photoURL = social?.photoURL ?? undefined;
    }
    try {
      const profile = RaiSsoProfile.fromLogin({ raisso, social, ente_accreditato, user_data_update, user_update })
      localStorageService.setProfile(profile);
      localStorageService.setToken({
        authToken: authorization,
        refreshToken: refreshToken
      });
      dispatchEvent(raiSsoLoginEvent, profile);
      broadcast(this.broadcastChannel, { action: 'login' });
      if (profile.user_update) {
        _event$.next(new CustomEvent(raiUserUpdate, { detail: profile, bubbles: true, cancelable: true }));
        dispatchEvent(raiUserUpdate, profile);
      }
      return profile;
    }
    catch (e) {
      if (e instanceof Error) { throw e; }
      console.error('[profile-manager] _setUserObject:', e);
      throw InvalidOperationError.fromRaisso(errorFactory(RaissoError, 'Opps! Qualcosa è andato storto', { cause: e, context: { data: JSON.stringify(data) } }))
    }
  }

  softLogout(eventNeeded: boolean, preventAutologin: boolean) {
    raisso.softLogout(eventNeeded, preventAutologin)
    broadcast(this.broadcastChannel, { action: 'logout' })
  }

  async softLogoutForEnti(pwd: string) {
    if (!raisso.checkLogin()) { return false; }
    const p = this.getUserProfile();
    if (!p) { return false; }
    const json = p.toJSON()
    type PJson = typeof json;
    try {
      const data = await this.sendLogin(p.email, pwd, DOMAIN_API_KEY, true);
      if (!data || !data.raisso) {
        return false;
      }
      const profile = RaiSsoProfile.fromJSON(Object.assign<PJson, Pick<PJson, 'user_type'>>(json, {
        user_type: 'persona'
      }))
      localStorageService.setProfile(profile, null);
      return raisso.softLogout(true, true);
    } catch (e) {
      console.error('[profile-manager] softLogoutForEnti:', e);
      const profile = RaiSsoProfile.fromJSON(Object.assign<PJson, Pick<PJson, 'user_type'>>(json, {
        user_type: 'ente'
      }))
      localStorageService.setProfile(profile);
      return false;
    }
  }

  logout() {
    try {
      this.softLogout(true,true);
      const prevUrl = getCookie('prevUrl') ?? new URLSearchParams(location.search).get('prevUrl');
      if (!!prevUrl && prevUrl.indexOf('user') == -1) {
        window.location.replace(prevUrl);
      }
    } catch(e) { /* empty */ }
  }

  async registration(data: RegistrationRequest) {
    if (!data.domainApiKey) {
      data.domainApiKey = DOMAIN_API_KEY
    }
    try {
      const response = await RegistrationController.instance.registration(data)
      const resp = R.getExn(response);
      return resp
    } catch (e) {
      if (e instanceof RaissoError) { throw InvalidResponseError.fromRaisso(e) }
      if (e instanceof Error) { throw e; }
      console.error('[profile-manager] registration:', e);
      throw InvalidOperationError.fromRaisso(errorFactory(RaissoError, 'Opps! Qualcosa è andato storto', { cause: e, context: { data: JSON.stringify(data) } }))
    }
  }

  async resendActivationMail(email: string) {
    try {
      const response = await RegistrationController.instance.resendActivationMail(email, DOMAIN_API_KEY)
      const resp = R.getExn(response);
      return resp
    } catch (e) {
      if (e instanceof RaissoError) { throw InvalidResponseError.fromRaisso(e) }
      if (e instanceof Error) { throw e }
      console.error('[profile-manager] resendActivation:', e);
      throw InvalidOperationError.fromRaisso(errorFactory(RaissoError, 'Opps! Qualcosa è andato storto', { cause: e, context: { data: email }}))
    }
  }

  async checkPrivacyPolicy() {
    let check = raisso.checkPrivacy();
    if (!check) {
      try {
        await this.getPrivacyPolicyInfo();
        check = raisso.checkPrivacy();
      } catch (e) { /* no-line */ }
    }
    return check;
  }
  /**
   *
   */
  async deleteUser() {
    if (!raisso.checkLogin()) {
      console.error('[profile-manager] deleteUser: Utente non loggato')
      throw NeedLoginError.fromRaisso(errorFactory(RaissoError, 'Utente non loggato', { context: { needLogin: true }}))
    }
    try {
      const response = await UserController.instance.deleteEmail()
      const resp = R.getExn(response);
      return resp.response === 'OK';
    } catch (e) {
      if (e instanceof RaissoError) { throw InvalidResponseError.fromRaisso(e) }
      if (e instanceof Error) { throw e; }
      console.error('[profile-manager] deleteUser:', e);
      throw InvalidOperationError.fromRaisso(errorFactory(RaissoError, 'Opps! Qualcosa è andato storto', { cause: e }))
    }
  }

  async pairingGetAppId(data: Pick<DeviceRequest, "userId"> & { marca: string, modello: 'web' }) {
    try {
      const _data: DeviceRequest = { ...data, domainApiKey: DOMAIN_API_KEY }
      const response = await DeviceController.instance.getAppId(_data)
      const resp = R.getExn(response);
      if (resp.response === 'OK' && resp.appID) {
        localStorageService.setDeviceAppId(resp.appID);
      }
      return resp
    } catch (e) {
      if (e instanceof RaissoError) { throw InvalidResponseError.fromRaisso(e) }
      if (e instanceof Error) { throw e }
      console.error('[profile-manager] pairingGetAppId:', e);
      throw InvalidOperationError.fromRaisso(errorFactory(RaissoError, 'Opps! Qualcosa è andato storto', { cause: e, context: { data: JSON.parse(JSON.stringify(data)) } }))
    }
  }

  async pairingInit(data: Pick<DeviceRequest, "userId"> & { marca: string, modello: 'web' }) {
    try {
      const appID = localStorageService.getDeviceAppId()
      if (Number.isNaN(appID)) {
        throw RequestNotAllowedError.fromRaisso(errorFactory(RaissoError, "AppID non valido", { context: { data: appID }}));
      }
      const _data: DeviceRequest = { ...data, domainApiKey: DOMAIN_API_KEY, appID };
      const response = await DeviceController.instance.pairingInit(_data)
      const resp = R.getExn(response);
      return resp
    } catch (e) {
      if (e instanceof RaissoError) { throw InvalidResponseError.fromRaisso(e) }
      if (e instanceof Error) { throw e }
      console.error('[profile-manager] pairingInit:', e);
      throw InvalidOperationError.fromRaisso(errorFactory(RaissoError, 'Opps! Qualcosa è andato storto', { cause: e, context: { data: JSON.parse(JSON.stringify(data)) } }))
    }
  }

  async pairing(data: DeviceRequest) {
    try {
      const response = await DeviceController.instance.pairing(data)
      const resp = R.getExn(response);
      return resp
    } catch (e) {
      if (e instanceof RaissoError) { throw InvalidResponseError.fromRaisso(e) }
      if (e instanceof Error) { throw e; }
      console.error('[profile-manager] pairing:', e);
      throw InvalidOperationError.fromRaisso(errorFactory(RaissoError, 'Opps! Qualcosa è andato storto', { cause: e, context: { data: JSON.parse(JSON.stringify(data)) } }))
    }
  }

  async pairingCheck(data: Pick<DeviceRequest, "userId" | "marca"> & { pin: number, modello?: "web" }) {
    try {
      const appID = localStorageService.getDeviceAppId()
      if (Number.isNaN(appID)) {
        throw RequestNotAllowedError.fromRaisso(errorFactory(RaissoError, "AppID non valido", { context: { data: appID }}));
      }
      const _data: DeviceRequest = { ...data, domainApiKey: DOMAIN_API_KEY, appID }
      const response = await DeviceController.instance.pairingCheck(_data)
      const resp = R.getExn(response);
      if (resp.rtkn) {
        localStorageService.setToken({ authToken: "", refreshToken: resp.rtkn });
      }
      return resp
    } catch (e) {
      if (e instanceof RaissoError) { throw InvalidResponseError.fromRaisso(e) }
      if (e instanceof Error) { throw e; }
      console.error('[profile-manager] pairingCheck:', e);
      throw InvalidOperationError.fromRaisso(errorFactory(RaissoError, 'Opps! Qualcosa è andato storto', { cause: e, context: { data: JSON.parse(JSON.stringify(data)) } }))
    }
  }

  async pairedDeviceList() {
    try {
      const response = await DeviceController.instance.list()
      const resp = R.getExn(response);
      return resp;
    } catch (e) {
      if (e instanceof RaissoError) { throw InvalidResponseError.fromRaisso(e) }
      if (e instanceof Error) { throw e; }
      console.error('[profile-manager] pairedDeviceList:', e);
      throw InvalidOperationError.fromRaisso(errorFactory(RaissoError, 'Opps! Qualcosa è andato storto', { cause: e }))
    }
  }

  async pairingRefreshToken(appId?: string, refreshToken?: string) {
    try {
      const refToken = refreshToken ?? localStorageService.getRefreshToken()
      if (!refToken) {
        console.error('[profile-manager] pairingRefreshToken: Refresh Token non trovato');
        throw RequestNotAllowedError.fromRaisso(errorFactory(RaissoError, 'Refresh Token non trovato'))
      }
      let _appId = appId;
      if (!_appId) {
        const app_id = localStorageService.getDeviceAppId()
        if (Number.isNaN(app_id)) {
          throw RequestNotAllowedError.fromRaisso(errorFactory(RaissoError, 'AppID non trovato'))
        }
        _appId = app_id.toString(10)
      }
      const response = await TokenController.instance.refresh({ domainApiKey: DOMAIN_API_KEY, refreshToken: refToken, appId: _appId })
      const data = R.getExn(response);
      if (data.token) {
        localStorageService.setToken({ authToken: data.token, refreshToken: refToken });
        localStorageService.setTokenLastDate();
      }
      return data
    } catch (e) {
      if (e instanceof RaissoError) { throw InvalidResponseError.fromRaisso(e) }
      if (e instanceof Error) { throw e; }
      console.error('[profile-manager] pairingRefreshToken:', e);
      throw errorFactory(RaissoError, 'Opps! Qualcosa è andato storto', { cause: e, context: { data: { appId, refreshToken } } })
    }
  }

  async unpairingDevice(authDevice?: string) {
    const authToken = authDevice ?? localStorageService.getAccessToken()
    if (!authToken) {
      console.error('[profile-manager] unpairingDevice: Authorization Token mancante')
      throw RequestNotAllowedError.fromRaisso(errorFactory(RaissoError, 'Authorization Token mancante', { context: { needLogin: true }}))
    }

    try {
      if (!raisso.checkLogin()) {
        throw NeedLoginError.fromRaisso(errorFactory(RaissoError, 'Richiesta l\'autenticazione', { context: { needLogin: true } }))
      }
      const response = await DeviceController.instance.unparing(authToken)
      const data = R.getExn(response);
      return data
    } catch (e) {
      if (e instanceof RaissoError) { throw InvalidResponseError.fromRaisso(e) }
      if (e instanceof Error) { throw e; }
      console.error('[profile-manager] unpairingDevice:', e);
      throw InvalidOperationError.fromRaisso(errorFactory(RaissoError, 'Opps! Qualcosa è andato storto', { cause: e, context: { data: authToken }}))
    }
  }

  async getNewsletter() {
    try {
      const response = await UserController.instance.serviceNl()
      const resp = R.getExn(response);
      return resp
    } catch (e) {
      if (e instanceof RaissoError) { throw InvalidResponseError.fromRaisso(e) }
      if (e instanceof Error) { throw e; }
      console.error('[profile-manager] getNewsletter:', e);
      throw InvalidOperationError.fromRaisso(errorFactory(RaissoError, 'Opps! qualcosa è andato storto', { cause: e }))
    }
  }

  async oAuthAuthorize(data: OauthLoginRequest): Promise<OauthCodeResponse> {
    try {
      const response = await GoogleOauthController.instance.authorize(data)
      const resp = R.handleError(response, err => ({ code: '500', state: err.code } satisfies OauthCodeResponse as OauthCodeResponse));
      return R.getExn(resp);
    } catch (e) {
      console.error('[profile-manager] oAuthAuthorize:', e);
      return { code: '500', state: 'Opps! Qualcosa è andato storto' };
    }
  }

  async saveAccessToken(token: string) {
    localStorageService.setToken({
      authToken: token,
      refreshToken: ""
    })
  }

  async parentalControlCreatePin(newPin: string ): Promise<GenericResponse> {
    try {
      if (!raisso.checkLogin()) {
        return {
          response: 'KO',
          esito: 'KO',
          message: 'Utente non loggato'
        };
      }
      const response = await ParentalControlController.instance.savePin(newPin);
      const resp = R.getExn(response);
      if (resp.esito === 'OK' || resp.response === 'OK') {
        let p = this.getUserProfile();
        if (!p) {
          p = await this.refreshUserDetails();
        }
        if (p && !p.parental_control) {
          const prof = p.toJSON();
          prof.parental_control = {
            pin: newPin,
            level: 0,
            created_date: new Date().toJSON() as DateTimeString,
            last_modified_date: null
          };
          localStorageService.setProfile(RaiSsoProfile.fromJSON(prof));
        }
      }
      return resp;
    } catch (e) {
      if (e instanceof RaissoError) { throw InvalidResponseError.fromRaisso(e) }
      if (e instanceof Error) { throw e; }
      console.error('[profile-manager] parentalControlCreatePin:', e);
      throw InvalidOperationError.fromRaisso(errorFactory(RaissoError, 'Opps! Qualcosa è andato storto', { cause: e, context: { data: JSON.stringify({ newPin}) } }))
    }
  }

  async parentalControlUpdatePin(newPin: string): Promise<GenericResponse> {
    try {
      if (!raisso.checkLogin()) {
        return {
          response: 'KO',
          esito: 'KO',
          message: 'Utente non loggato'
        };
      }
      const p = this.getUserProfile()
      const currentPin = p?.parental_control?.pin;
      if (!currentPin) {
        return {
          response: 'KO',
          esito: 'KO',
          message: 'Pin non presente. Creare il PIN'
        };
      }
      const response = await ParentalControlController.instance.updatePin({
        currentPin,
        newPin,
        confirmNewPin: newPin,
        level: String(p.parental_control.level)
      })
      const resp = R.getExn(response);
      if ((resp.esito === 'OK' || resp.response === 'OK') && p.parental_control) {
        p.parental_control.pin = newPin;
        p.parental_control.last_modified_date = new Date().toJSON() as DateTimeString;
        localStorageService.setProfile(p);
      }
      return resp;
    } catch (e) {
      if (e instanceof RaissoError) { throw InvalidResponseError.fromRaisso(e) }
      if (e instanceof Error) { throw e; }
      console.error('[profile-manager] parentalControlCreatePin:', e);
      throw InvalidOperationError.fromRaisso(errorFactory(RaissoError, 'Opps! Qualcosa è andato storto', { cause: e, context: { data: JSON.stringify({ newPin }) } }))
    }
  }

  async parentalControlUpdateLevel(level: number): Promise<GenericResponse> {
    try {
      if (!raisso.checkLogin()) {
        return {
          response: 'KO',
          esito: 'KO',
          message: 'Utente non loggato'
        };
      }
      const p = this.getUserProfile()
      const currentPin = p?.parental_control?.pin;
      if (!currentPin) {
        return {
          response: 'KO',
          esito: 'KO',
          message: 'Pin non presente. Creare il PIN'
        };
      }

      const response = await ParentalControlController.instance.updateLevel({
        currentPin,
        level,
      });
      const resp = R.getExn(response);
      if ((resp.esito === 'OK' || resp.response === 'OK') && p.parental_control) {
        p.parental_control.level = level;
        p.parental_control.last_modified_date = new Date().toJSON() as DateTimeString;
        localStorageService.setProfile(p);
      }
      return resp;
    } catch (e) {
      if (e instanceof RaissoError) { throw InvalidResponseError.fromRaisso(e) }
      if (e instanceof Error) { throw e; }
      console.error('[profile-manager] parentalControlCreatePin:', e);
      throw InvalidOperationError.fromRaisso(errorFactory(RaissoError, 'Opps! Qualcosa è andato storto', { cause: e, context: { data: JSON.stringify({ level }) } }))
    }
  }

  async parentalControlDelete(): Promise<GenericResponse> {
    try {
      if (!raisso.checkLogin()) {
        return {
          response: 'KO',
          esito: 'KO',
          message: 'Utente non loggato'
        };
      }
      const p = this.getUserProfile()
      const currentPin = p?.parental_control?.pin;
      if (!currentPin) {
        return {
          response: 'KO',
          esito: 'KO',
          message: 'Pin non presente. Creare il PIN'
        };
      }
      const response = await ParentalControlController.instance.delete(currentPin);
      const resp = R.getExn(response);
      if (resp.esito === 'OK' || resp.response === 'OK') {
        const profileJSON = p.toJSON();
        profileJSON.parental_control = undefined;
        const updatedProfile = RaiSsoProfile.fromJSON(profileJSON);
        localStorageService.setProfile(updatedProfile);
      }
      return resp;
    } catch (e) {
      if (e instanceof RaissoError) { throw InvalidResponseError.fromRaisso(e) }
      if (e instanceof Error) { throw e; }
      console.error('[profile-manager] parentalControlCreatePin:', e);
      throw InvalidOperationError.fromRaisso(errorFactory(RaissoError, 'Opps! Qualcosa è andato storto', { cause: e }))
    }
  }

  async parentalControlRequestResetPin(): Promise<GenericResponse> {
    try {
      if (!raisso.checkLogin()) {
        return {
          response: 'KO',
          esito: 'KO',
          message: 'Utente non loggato'
        };
      }
      const response = await ParentalControlController.instance.requestPinReset();
      const resp = R.getExn(response);
      await this.refreshUserDetails();
      return resp;
    } catch (e) {
      if (e instanceof RaissoError) { throw InvalidResponseError.fromRaisso(e) }
      if (e instanceof Error) { throw e; }
      throw InvalidOperationError.fromRaisso(errorFactory(RaissoError, 'Opps! Qualcosa è andato storto', { cause: e }))
    }
  }

  async parentalControlResetPin(newPin: string, tokenReset: string): Promise<GenericResponse> {
    const accessToken = localStorageService.getAccessToken()
    if (!accessToken) {
      throw NeedLoginError.fromRaisso(errorFactory(RaissoError, 'User not logged in', { context: { needLogin: true } }));
    }
    try {
      if (!raisso.checkLogin()) {
        return {
          response: 'KO',
          esito: 'KO',
          message: 'Utente non loggato'
        };
      }
      const p = this.getUserProfile()
      const currentPin = p?.parental_control?.pin;
      if (!currentPin) {
        return {
          response: 'KO',
          esito: 'KO',
          message: 'Pin non presente. Creare il PIN'
        };
      }
      const response = await ParentalControlController.instance.resetPin({
        newPin,
        confirmNewPin: newPin
      }, accessToken, tokenReset)
      const resp = R.getExn(response);
      if ((resp.esito === 'OK' || resp.response === 'OK') && p.parental_control) {
        p.parental_control.pin = newPin;
        p.parental_control.last_modified_date = new Date().toJSON() as DateTimeString;
        localStorageService.setProfile(p);
      }
      return resp;
    } catch (e) {
      if (e instanceof RaissoError) { throw InvalidResponseError.fromRaisso(e) }
      if (e instanceof Error) { throw e; }
      console.error('[profile-manager] parentalControlCreatePin:', e);
      throw InvalidOperationError.fromRaisso(errorFactory(RaissoError, 'Opps! Qualcosa è andato storto', { cause: e, context: { data: JSON.stringify({ newPin }) } }))
    }
  }

  async parentalControlCheckTokenReset(tokenReset: string): Promise<GenericResponse> {
    const accessToken = localStorageService.getAccessToken()
    if (!accessToken) {
      throw NeedLoginError.fromRaisso(errorFactory(RaissoError, 'User not logged in', { context: { needLogin: true } }));
    }
    try {
      if (!raisso.checkLogin()) {
        return {
          response: 'KO',
          esito: 'KO',
          message: 'Utente non loggato'
        };
      }
      const p = this.getUserProfile()
      const currentPin = p?.parental_control?.pin;
      if (!currentPin) {
        return {
          response: 'KO',
          esito: 'KO',
          message: 'Pin non presente. Creare il PIN'
        };
      }
      const response = await ParentalControlController.instance.checkTokenResetPin(accessToken, tokenReset)
      const resp = R.getExn(response);
      return resp;
    } catch (e) {
      if (e instanceof RaissoError) { throw InvalidResponseError.fromRaisso(e) }
      if (e instanceof Error) { throw e; }
      console.error('[profile-manager] parentalControlCheckTokenReset:', e);
      throw InvalidOperationError.fromRaisso(errorFactory(RaissoError, 'Opps! Qualcosa è andato storto'))
    }
  }
}

const profileManager = new RaiSsoProfileManager();

export { profileManager };
